import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";
import { notifyApiError } from "../../lib/toast-error";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useDeactivateMasterData, useStaleTags, type AdminTag } from "./api";

/**
 * [GR-008] "Pflegeverdaechtige Tags": the active tags not used for more than
 * 12 months - last used before then, or never used and created before then -
 * "damit der Admin sie bewerten und deaktivieren kann". Deactivating one here
 * is the same audited deactivation as in the Tags tab, and as reversible.
 */
export function StaleTagsPanel(): JSX.Element {
  const { t } = useTranslation();
  const stale = useStaleTags();
  const deactivate = useDeactivateMasterData("tags");
  const [pending, setPending] = useState<AdminTag | null>(null);
  const rows = stale.data ?? [];

  const confirm = (): void => {
    if (!pending) return;
    deactivate.mutate(pending.id, {
      onSuccess: () => {
        toast.success(t("adminMasterData.deactivated"));
        setPending(null);
      },
      onError: (error) => {
        setPending(null);
        notifyApiError(error, {
          precondition: t("errors.precondition"),
          forbidden: t("errors.forbidden"),
          conflict: t("errors.conflict"),
          generic: t("errors.saveFailed"),
        });
      },
    });
  };

  return (
    <div data-testid="admin-master-data_stale-tags" className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("adminMasterData.staleTags.intro")}</p>
      {stale.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : stale.isError ? (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
          <span>{t("errors.load")}</span>
          <Button variant="outline" size="sm" data-testid="admin-master-data_stale-tags-retry" onClick={() => void stale.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p data-testid="admin-master-data_stale-tags-empty" className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t("adminMasterData.staleTags.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table data-testid="admin-master-data_stale-tags-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("adminMasterData.colName")}</TableHead>
                <TableHead>{t("adminMasterData.colCreatedAt")}</TableHead>
                <TableHead>{t("adminMasterData.colLastUsedAt")}</TableHead>
                <TableHead>{t("adminMasterData.colUsageCount")}</TableHead>
                <TableHead className="text-right">{t("adminMasterData.colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((tag) => (
                <TableRow key={tag.id} data-testid={`admin-master-data_stale-tag_${tag.id}`}>
                  <TableCell className="font-medium">{tag.name}</TableCell>
                  <TableCell>{formatDate(tag.createdAt)}</TableCell>
                  <TableCell>{tag.lastUsedAt ? formatDate(tag.lastUsedAt) : t("adminMasterData.staleTags.neverUsed")}</TableCell>
                  <TableCell>{tag.usageCount}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`admin-master-data_stale-tag-deactivate_${tag.id}`}
                      onClick={() => setPending(tag)}
                    >
                      {t("adminMasterData.deactivate")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={pending !== null} onOpenChange={(open) => (open ? undefined : setPending(null))}>
        <DialogContent data-testid="admin-master-data_stale-tag-dialog">
          <DialogHeader>
            <DialogTitle>{t("adminMasterData.deactivateDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("adminMasterData.deactivateDialog.question", { name: pending?.name ?? "" })}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("adminMasterData.deactivateDialog.consequence")}</p>
          <DialogFooter>
            <Button variant="outline" data-testid="admin-master-data_stale-tag-cancel" onClick={() => setPending(null)}>
              {t("common.cancel")}
            </Button>
            <Button data-testid="admin-master-data_stale-tag-confirm" onClick={confirm} disabled={deactivate.isPending}>
              {t("adminMasterData.deactivate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
